import { forwardRef, useState, useEffect } from "react";
import * as t from "@rekajs/types";
import {
  LayoutGridIcon,
  InstagramIcon,
  ArrowRight,
  Search,
  Loader2,
} from "lucide-react";
import { getLangDir } from "rtl-detect";
import type { ComponentType } from "@repo/editor/types";
import type {
  ComponentSettings,
  ComponentSettingsProps,
} from "@repo/editor/externalComponents";
import { Link } from "@repo/i18n/routing";
import { useRouter } from "next/router";
import { observer } from "mobx-react-lite";
import { useApp } from "@repo/app/useApp";
import type { SearchData } from "@repo/fetch-repository/search";
import { SearchRepository } from "@repo/fetch-repository/search";
import { useLocale, useTranslations } from "next-intl";
import type { BreadCrumb } from "@repo/fetch-repository/factory";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectGroup,
  SelectItem,
  SelectContent,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Button } from "../components/ui/button";
import { Menu } from "../components/common/menu";
import CroppedSection from "../components/common/cropped-section";
import VideoPlayer from "../components/common/video-player";
import TypingAnimation from "../hooks/use-typewriter";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

export const headerDefinition: ComponentSettingsProps = {
  title: {
    type: "string",
    section: "متن",
    label: "عنوان",
  },
  description: {
    type: "string",
    section: "متن",
    label: "توضیحات",
  },
  buttonText: {
    type: "string",
    section: "متن",
    label: "متن دکمه",
  },
  buttonLink: {
    type: "string",
    section: "متن",
    label: "لینک دکمه",
    default: "https://www.google.com",
  },
  socials: {
    type: "boolean",
    label: "شبکه های اجتماعی",
  },
  backgroundImage: {
    type: "image",
    label: "تصویر پس زمینه",
  },
  subTitle: {
    type: "string",
    section: "متن",
    label: "زیرعنوان",
  },
  alt: {
    type: "string",
    label: "عنوان تصویر",
    default: "تصویر پس زمینه",
  },
};
export interface HeaderProps {
  title: string;
  breadCrumbs?: BreadCrumb[] | null;
  description?: string;
  buttonText?: string;
  socials?: boolean;
  backgroundImage?: string;
  alt?: string;
  subTitle?: string;
  buttonLink?: string;
}

const HeaderComponent = forwardRef<HTMLDivElement, HeaderProps>(
  (props, ref) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [searchResults, setSearchResults] = useState<SearchData>();
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const router = useRouter();
    const locale = useLocale();
    const app = useApp();
    const translations = useTranslations();
    const dir = getLangDir(locale);
    const isEditor = app.rendererMode === "editor";
    const isPage: boolean =
      ["about-company", "contact-us", "reviews"].findIndex((item) =>
        router.asPath.includes(item)
      ) > -1;
    const temp = props.description?.split(">");
    const pagesBreadCrumbs: BreadCrumb[] = [];
    if (isPage) {
      temp?.forEach((item, index) => {
        pagesBreadCrumbs.push({
          label: item,
          url: index === 0 ? "/" : router.asPath,
        });
      });
    }

    useEffect(() => {
      const search = async (): Promise<void> => {
        if (searchQuery.length < 2) {
          setSearchResults(undefined);
          return;
        }

        setIsSearching(true);
        try {
          const repository = new SearchRepository(locale);
          const response = await repository.getMany({
            query: searchQuery,
          });

          if (response.succeeded && response.data) {
            setSearchResults(response.data);
          }
        } finally {
          setIsSearching(false);
        }
      };

      const timeoutId = setTimeout(() => void search(), 300);
      return () => {
        clearTimeout(timeoutId);
      };
    }, [searchQuery, locale]);

    const renderSearchResults = ({
      products,
      services,
      projects,
      suppliers,
      posts,
      teams,
      pages,
      menus,
      reviews,
      licences,
      catalogs,
    }: {
      products: string;
      services: string;
      projects: string;
      suppliers: string;
      posts: string;
      teams: string;
      pages: string;
      menus: string;
      reviews: string;
      licences: string;
      catalogs: string;
    }): React.JSX.Element | null => {
      if (!searchResults) return null;

      const renderItems = (
        items: SearchData["products"],
        title: string,
        withLink?: boolean
      ): React.JSX.Element | null => {
        const customLink = withLink ?? true;
        if (!items.length) return null;
        return (
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-500 mb-2 rtl:font-doran">
              {title}
            </h3>
            <div className="space-y-2">
              {items.map((item) => (
                <Link
                  className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg transition-colors rtl:font-doran"
                  href={customLink ? item.link : ""}
                  key={item.id}
                  onClick={() => {
                    setIsSearchOpen(false);
                  }}
                >
                  <div className="flex-1">
                    <p className="text-sm font-medium">{item.name}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        );
      };

      return (
        <div className="max-h-[60vh] overflow-y-auto">
          {renderItems(searchResults.products, products)}
          {renderItems(searchResults.services, services)}
          {renderItems(searchResults.projects, projects)}
          {renderItems(searchResults.suppliers, suppliers)}
          {renderItems(searchResults.posts, posts)}
          {renderItems(searchResults.teams, teams)}
          {renderItems(searchResults.pages, pages)}
          {renderItems(searchResults.menus, menus)}
          {renderItems(searchResults.reviews, reviews)}
          {renderItems(searchResults.licences, licences)}
          {renderItems(searchResults.catalogs, catalogs)}
        </div>
      );
    };

    return (
      <>
        <Menu
          onChange={(val: boolean) => {
            setIsMenuOpen(val);
          }}
          open={isMenuOpen}
        />
        <div
          className="w-full h-full relative"
          ref={ref}
          style={{
            marginBottom: "calc(-27.22% / 2)",
          }}
        >
          {props.backgroundImage?.includes("mp4") ? (
            <div className="top-0 left-0 right-0 bottom-0 absolute header-bg -z-20">
              <VideoPlayer link={props.backgroundImage || ""} />
            </div>
          ) : (
            <div
              className="top-0 left-0 right-0 bottom-0 absolute header-bg -z-20 bg-fixed"
              style={{
                backgroundImage: `url(${props.backgroundImage || "/images/header-bg.png"})`,
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
                backgroundSize: "cover",
              }}
            />
          )}

          <div className="lg:px-24 lg:max-w-full container relative mx-auto flex flex-col gap-16 xl:gap-28 lg:gap-20 md:gap-16 pt-12">
            {props.socials ? (
              <div
                className="absolute xl:-left-10 left-5 lg:top-full bottom-1/4 flex "
                dir="ltr"
              >
                <div className="size-full relative flex items-center justify-center">
                  <div className="absolute rotate-90 font-thin lg:text-xl text-xs px-2 text-gray-300 flex items-center">
                    <span className="mx-2 uppercase font-normal -mt-3 whitespace-nowrap">
                      Scroll
                    </span>
                    <span className="lg:w-28 w-20 bg-white h-px ml-2 -mr-2" />
                    <ArrowRight className="size-3" />
                    <InstagramIcon className="lg:ml-10 ml-5 size-5" />
                  </div>
                </div>
              </div>
            ) : null}
            <div className="flex w-full items-center justify-between">
              <Link
                href="/"
                onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                  if (isEditor) {
                    e.preventDefault();
                  }
                }}
              >
                <img
                  alt={props.alt || String(headerDefinition.alt.default)}
                  className="w-full max-w-[99px] md:max-w-[190px] lg:max-w-[257px] "
                  onError={(e) => {
                    e.currentTarget.src = "/images/placeholder.png";
                  }}
                  src="/images/logo.png"
                />
              </Link>
              <div className="flex text-white gap-4">
                <div className="flex items-center justify-center border-white">
                  <Search
                    className="lg:w-6 lg:h-6 w-4 h-4 md:w-5 md:h-5 cursor-pointer"
                    onClick={() => {
                      setIsSearchOpen(true);
                    }}
                  />
                </div>
                <div className="border-white pl-3">
                  <span className="text-[16px] md:text-[20px] lg:text-[24px] font-[100] leading-[1.38]">
                    <Select
                      defaultValue={router.locale}
                      onValueChange={(val) => {
                        void router.push(router.pathname, router.asPath, {
                          locale: val,
                        });
                      }}
                      open={isEditor ? false : undefined}
                    >
                      <SelectTrigger
                        className="border-none outline-none focus:ring-0"
                        onClick={(e) => {
                          if (isEditor) {
                            e.preventDefault();
                          }
                        }}
                      >
                        <SelectValue placeholder="EN" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectItem
                            className="border-none outline-none focus:ring-0"
                            value="en"
                          >
                            English
                          </SelectItem>
                          <SelectItem
                            className="border-none outline-none focus:ring-0"
                            value="fa"
                          >
                            فارسی
                          </SelectItem>
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </span>
                </div>
                <div className="flex items-center justify-center border-white">
                  <LayoutGridIcon
                    className="lg:w-6 lg:h-6 w-4 h-4 md:w-5 md:h-5 cursor-pointer"
                    onClick={() => {
                      if (isEditor) {
                        return;
                      }
                      setIsMenuOpen(true);
                    }}
                  />
                </div>
              </div>
            </div>
            <div className="container flex 2xl:px-16 lg:px-24 md:px-20 px-4">
              <div className="flex flex-col gap-3">
                <h1 className="font-helvetica rtl:font-doran text-white text-2xl lg:text-5xl font-thin rtl:font-medium leading-relaxed mb-2">
                  {props.title}
                </h1>
                {/* eslint-disable-next-line no-nested-ternary -- disable nested ternary */}
                {isPage ? (
                  <div>
                    {pagesBreadCrumbs.map((breadcrumb, index) => (
                      <div className="inline-block" key={index}>
                        <Link
                          className="font-helvetica rtl:font-doran text-sm lg:text-2xl text-primary font-light rtl:font-medium uppercase"
                          href={breadcrumb.url}
                        >
                          {breadcrumb.label}
                        </Link>
                        {index + 1 !== pagesBreadCrumbs.length ? (
                          <span className="-mt-2 mx-4 font-helvetica rtl:font-doran text-sm lg:text-2xl text-primary font-light rtl:font-medium uppercase">
                            {">"}
                          </span>
                        ) : null}
                      </div>
                    ))}
                  </div>
                ) : // eslint-disable-next-line no-nested-ternary -- disable nested ternary
                props.breadCrumbs?.length ? (
                  <div>
                    {props.breadCrumbs.map((breadcrumb, index) => (
                      <div className="inline-block" key={index}>
                        <Link
                          className="font-helvetica rtl:font-doran text-sm lg:text-2xl text-primary font-light rtl:font-medium uppercase"
                          href={breadcrumb.url}
                        >
                          {breadcrumb.label}
                        </Link>
                        {index + 1 !== props.breadCrumbs?.length ? (
                          <span className="-mt-2 mx-4 font-helvetica rtl:font-doran text-sm lg:text-2xl text-primary font-light rtl:font-medium uppercase">
                            {">"}
                          </span>
                        ) : null}
                      </div>
                    ))}
                  </div>
                ) : props.description ? (
                  <p className="font-helvetica rtl:font-doran text-sm lg:text-2xl text-primary font-light rtl:font-medium uppercase">
                    {props.description}
                  </p>
                ) : null}

                {props.buttonText ? (
                  <div className="lg:pt-5 pt-3 lg:-mb-20">
                    <Button asChild className="uppercase">
                      <Link
                        href={
                          props.buttonLink ||
                          String(headerDefinition.buttonLink.default)
                        }
                        target="_blank"
                      >
                        {props.buttonText}
                      </Link>
                    </Button>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
          <div className="relative w-full -bottom-px -z-10 xl:-mt-16 md:-mt-4">
            <CroppedSection animate />
          </div>
        </div>
        {props.subTitle ? (
          <div className="text-right container mx-auto pt-5 md:pt-8 lg:pt-11 relative z-10">
            <h2 className="hover:text-primary font-helvetica rtl:font-doran text-[14px] md:text-[26px] lg:text-[31px] text-[#7E7E7E] font-light rtl:font-medium uppercase">
              <TypingAnimation speed={50} text={props.subTitle} />
            </h2>
          </div>
        ) : null}

        <Dialog onOpenChange={setIsSearchOpen} open={isSearchOpen}>
          <DialogContent className="max-w-2xl" dir={dir}>
            <DialogHeader className="space-y-4">
              <div className="flex justify-between items-center">
                <DialogTitle className="rtl:font-doran ltr:font-helvetica">
                  {translations("search.title")}
                </DialogTitle>
              </div>
              <div className="relative">
                <Input
                  className="w-full pr-10 pl-4 py-2 border-2 focus:border-primary"
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                  }}
                  placeholder={translations("search.placeholder")}
                  value={searchQuery}
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {isSearching ? (
                    <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
                  ) : (
                    <Search className="w-5 h-5 text-gray-400" />
                  )}
                </div>
              </div>
            </DialogHeader>

            <div className="mt-4">
              {isSearching ? (
                <div className="flex justify-center items-center py-8">
                  <Loader2 className="w-8 h-8 text-primary animate-spin" />
                </div>
              ) : (
                renderSearchResults({
                  products: translations("search.products"),
                  services: translations("search.services"),
                  projects: translations("search.projects"),
                  suppliers: translations("search.suppliers"),
                  posts: translations("search.posts"),
                  teams: translations("search.teams"),
                  pages: translations("search.pages"),
                  menus: translations("search.menus"),
                  reviews: translations("search.reviews"),
                  licences: translations("search.licences"),
                  catalogs: translations("search.catalogs"),
                })
              )}
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  }
) as ComponentType<HeaderProps>;

HeaderComponent.displayName = "Header";

export const Header = observer(
  HeaderComponent
) as unknown as ComponentType<HeaderProps>;
export const headerSettings: ComponentSettings = {
  name: "EHeader",
  props: headerDefinition,
  label: "هدر",
  widgets: {
    label: "هدر",
    template: () =>
      t.componentTemplate({
        component: t.identifier({ name: "EHeader", external: true }),
      }),
  },
};

Header.componentSettings = headerSettings;
